import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "../components/Seo";
import articleHtml from "../content/insights/securing-cloud-applications-ai-agents.html?raw";

const META = {
  title: "Securing Cloud Applications in the Age of AI Agents | Zero Harm AI",
  description:
    "A practical six-layer framework for securing AI workloads in the cloud: Identity, Connectivity, Execution, Credentials, Data, and Detection.",
  url: "https://www.zeroharmai.com/insights/securing-cloud-applications-ai-agents",
  datePublished: "2026-10-29",
};

export default function SecuringCloudAIAgents() {
  return (
    <>
      <Seo
        title={META.title}
        description={META.description}
        path="/insights/securing-cloud-applications-ai-agents"
        type="article"
        image="https://www.zeroharmai.com/insights/securing-cloud-applications-ai-agents/media/image1.png"
      />
      <Helmet>
        <meta property="article:published_time" content={META.datePublished} />
        <meta property="article:author" content="Dzung Tran" />
        <meta property="article:section" content="AI Security" />

        {/* Article schema markup */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TechArticle",
          "headline": "Securing Cloud Applications in the Age of AI Agents",
          "description": META.description,
          "url": META.url,
          "datePublished": META.datePublished,
          "dateModified": META.datePublished,
          "image": "https://www.zeroharmai.com/insights/zero-harm-ai.png",
          "author": {
            "@type": "Person",
            "name": "Dzung Tran",
            "url": "https://www.zeroharmai.com/about"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Zero Harm AI",
            "url": "https://www.zeroharmai.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.zeroharmai.com/zero-harm-ai-logo.png"
            }
          },
          "articleSection": "AI Security",
          "keywords": [
            "AI security", "AI agents", "cloud security",
            "prompt injection", "least privilege", "workload identity",
            "secrets management", "VPC", "detection and response"
          ]
        })}</script>
      </Helmet>

      <article className="max-w-4xl mx-auto px-4 py-10">
        <Link
          to="/insights"
          className="inline-block text-sm font-semibold text-blue-700 hover:text-blue-800 mb-8"
        >
          ← Back to Insights
        </Link>

        <header className="mb-8 border-b border-gray-200 pb-8">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-700 mb-3">
            AI Security
          </p>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            Securing Cloud Applications in the Age of AI Agents
          </h1>
          <p className="text-gray-600">
            A practical framework for Identity, Connectivity, Execution, Credentials, Data, and Detection.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            By Dzung Tran &nbsp;·&nbsp; Published October 29, 2026
          </p>
        </header>

        <div
          className="insight-article"
          dangerouslySetInnerHTML={{ __html: articleHtml }}
        />

        <section className="mt-12 bg-blue-50 border border-blue-100 rounded-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Securing AI agents in your cloud?
          </h2>
          <p className="text-gray-700 mb-5">
            Zero Harm AI helps teams design AI workloads with least-privilege access, controlled
            connectivity, and the detection to catch misuse early.
          </p>
          <Link
            to="/contact"
            className="inline-block px-5 py-2.5 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-semibold"
          >
            Talk to Zero Harm AI
          </Link>
        </section>
      </article>
    </>
  );
}
